import { useCallback, useContext, useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import moment from "moment";

import { CardRegistry } from "./TradeCard/CardRegistry";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";

import TopHeader from "./TopHeader";
import HeaderAccount from "./HeaderAccount";
import MiniHeader from "./MiniHeader";
import Loading from "../../../Utils/Loading/Loading";
import NotFound from "../../../Utils/NotFound/NotFound";
import Socket from "../../../context/SocketContext";

// Initializing outside to avoid re-creation on every render
const backendInstance = new Backend();

export default function Trade() {
  const windowSize = useSize();
  const socket = useContext(Socket);

  // --- State Management ---
  const [loading, setLoading] = useState(false);
  const [trade, setTrade] = useState([]);
  const [commission, setCommission] = useState(0);
  const [booked, setBooked] = useState(0);
  const [rent, setRent] = useState(0);
  const [commissionShow, setCommissionShow] = useState(false);
  const [isDollar, setIsDollar] = useState(0);
  const [sortBy, setSortBy] = useState("time_desc");

  // Date range state
  const [start] = useState(moment().startOf("week"));
  const [end] = useState(moment().endOf("week"));

  // --- Data Loading Logic ---
  const loadTrade = useCallback(
    async (startDate = start, endDate = end) => { 
      setLoading(true);

      try {
        const backend = await backendInstance.init();
        
        // Fetching credentials directly to keep dependencies flat
        const params = {
          server: localStorage.getItem("server"),
          id: localStorage.getItem("id"),
          user_id: localStorage.getItem("id"),
          username: localStorage.getItem("username"),
          token: localStorage.getItem("token"),
          start: startDate,
          end: endDate,
        };

        const res = await backend.trade(params);

        if (res?.error === "False") {
          setTrade(res.order || []);
          setCommission(res.commission || 0);
          setBooked(res.booked || 0);
          setRent(res.rent || 0);
          setCommissionShow(res.commission_show || false);
          setIsDollar(res.isDollar || 0);
        } else {
          if (res?.logout === 1) {
            window.LogOut?.();
          } else {
            toast.error(res?.message || "Failed to load trade data.");
          }
        }
      } catch (err) {
        console.error("Trade Load Error:", err);
        toast.error("Something went wrong while loading trade data.");
      } finally {
        setLoading(false);
      }
    },
    [start, end]
  );

  // --- Memoized Sorting ---
  // This derived state ensures the UI stays sorted even when 'trade' updates via socket
  const sortedTrade = useMemo(() => {
    const data = [...trade];
    switch (sortBy) {
      case "pl_high":
        return data.sort((a, b) => b.profit_loss - a.profit_loss);
      case "pl_low":
        return data.sort((a, b) => a.profit_loss - b.profit_loss);
      case "symbol_asc":
        return data.sort((a, b) => (a.symbol || "").localeCompare(b.symbol || ""));
      case "symbol_desc":
        return data.sort((a, b) => (b.symbol || "").localeCompare(a.symbol || ""));
      case "time_asc":
        return data.sort((a, b) => a.date_created - b.date_created);
      case "time_desc":
      default:
        return data.sort((a, b) => b.date_created - a.date_created);
    }
  }, [trade, sortBy]);

  // --- Side Effects & Sockets ---
  useEffect(() => {
    loadTrade();

    const handleReload = (payload) => {
      const currentUserId = localStorage.getItem("id");
      if (currentUserId === String(payload.id)) {
        loadTrade();
      }
    };

    socket.on("reload", handleReload);
    return () => socket.off("reload", handleReload);
  }, [loadTrade, socket]);

  // --- UI Calculations ---
  const contentHeight = windowSize[1] - 210;
  const CardComponent = CardRegistry[window.SERVER] || CardRegistry.DEFAULT;

  return (
    <div className="bg-[var(--background)] overflow-hidden">
      {/* Header with sorting */}
      <TopHeader
        sortBy={sortBy}
        onSort={setSortBy} // Pass setSortBy directly
        reload={(s, e) => loadTrade(s, e)}
      />

      <HeaderAccount
        booked={booked}
        commission={commission}
        rent={rent}
        commissionShow={commissionShow}
        isDollar={isDollar}
      />

      <div className="border-t border-[var(--hover)]" />

      <div className="px-5 py-3">
        <MiniHeader reload={loadTrade} />
        <div
          key={loading ? "loading" : "loaded"}
          style={{
            height: contentHeight,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center w-full h-full">
              <Loading height={contentHeight} />
            </div>
          ) : sortedTrade.length > 0 ? (
            <div
              className="custom-scrollbar"
              style={{
                height: contentHeight,
                overflowY: "auto", // auto is generally safer than scroll
                scrollBehavior: "smooth",
                paddingBottom: 200,
              }}
            >
              {sortedTrade.map((item, index) => (
                <CardComponent
                  key={item.id || index}
                  item={item}
                  index={index + 1}
                  isDollar={isDollar}
                />
              ))}
            </div>
          ) : (
            <NotFound
              small
              height={contentHeight}
              title="Nothing here"
              subtext="No trade found."
            />
          )}
        </div>
      </div>
    </div>
  );
}